/**
*
*   Collection List
*       - The Collection Data on the collection page
*
**/
'use client';

import Config from '@/config';
import Button from '@/components/button';
import Preloader from '@/components/preloader';
import { header } from '@/components/header/wrapper';
import ProductCard from '@/components/collection/product-card';
import Advertisement from '@/components/collection/advertisement';
import Translation from '@/components/translation';
import CurrencySwitcher from '@/components/cart/currency-switcher';
import { useCookies } from 'next-client-cookies';
import { useLocale, useTranslations } from 'next-intl';
import { useState, useRef } from 'react';


//Build the Collection
export default function Collection({ data }){

	//Prep State
	const t         = useTranslations();
	const locale    = useLocale();
	const Cookies   = useCookies();

	//The Loading State
	const [ loading , setLoading ] = useState(false);

	//The Filters
	const [ filters , setFilters ] = useState(((filters)=>{
		data.filters.forEach( filter => {
			filters[ filter.name[ locale ].toLowerCase() ] = '';
		});
		return filters;
	})({}));

	//Remove all hidden products and the gift card
	data.products = data.products.filter( product => {
		return product.tags.find( tag => tag.toLowerCase() == 'hidden') === undefined && product.handle != 'gift-card';
	});

	//The Products
	const [ products , setProducts ] = useState([ ...data.products ]);

	//The Purchase Type
	const [ currency , setCurrency ] = useState( ( Cookies.get('currency') || 'fiat' ) );

	//The Filter Functions
	const Filter = {

		$el: useRef(),

		/**
		*
		*   change
		*       - Update the products based on the filter settings
		*
		*   Params:
		*       n/a
		*
		**/
		change: (e) => {

			//Set as Loading
			setLoading(true);

			//The Updated Filters
			const updated = {
				...filters,
				[e.target.name]: e.target.value
			};

			//The Product List
			const list = [ ...data.products ];

			//Loop
			for( let i = ( list.length - 1); i >= 0; i--){
				(( product , i ) => {
					for( const key in updated ){
						if(
							updated[key].length > 0 &&
							( !product.filter[key] || product.filter[key].indexOf( updated[key] ) === -1 )
						){
							list.splice(i , 1);
							break;
						}
					}
				})( list[i] , i );
			}

			//Update the filters
			setFilters( updated );

			//Set the Products
			setProducts([ ...list ]);

			//Remove the Loading
			window.setTimeout( () => setLoading(false) ,1000);

		},

		/**
		*
		*   change
		*       - Update the products based on the filter settings
		*
		*   Params:
		*       n/a
		*
		**/

		reset: () => {

			//Set it as Loading
			setLoading(true);

			//Update the Filters
			setFilters(
				((filters)=>{
					data.filters.forEach( filter => {
						filters[ filter.name[ locale ].toLowerCase() ] = '';
					});
					return filters;
				})({})
			);

			//Set the Products
			setProducts([ ...data.products ]);

			//Remove the Loading
			window.setTimeout( () => setLoading(false) ,1000);

		},

		//Mobile Modal Options
		mobile: {

			//Open the Window
			open: () => {

				const $filter = Filter.$el.current.querySelector('[data-filters-mobile]');
				const $header = header.current;

				$header.classList.add('locked');

				$filter.classList.add('open');

			},

			//Open the Window
			close: () => {

				const $filter = Filter.$el.current.querySelector('[data-filters-mobile]');
				const $header = header.current;

				$header.classList.remove('locked');

				$filter.classList.remove('open');

			},

			//Allow clicking outside the modal
			closeModal: (e) => {

				const $filter = Filter.$el.current.querySelector('[data-filters-mobile]');

				if( e.target == $filter ) Filter.mobile.close();

			}

		},

		/**
		*
		*   currency
		*       - Update the currency type used
		*
		*   Params:
		*       n/a
		*
		**/
		currency: ( currency ) => {

			//Set as Loading
			setLoading(true);

			//Change the Currency
			setCurrency( currency );

			//Delay and remove loading
			window.setTimeout( () => setLoading(false) ,1000);

		}

	};

	//Return
	return (
		<>
			{/* Filters */}
			<section id="filters" className="container" ref={Filter.$el}>
				<div data-view="desktop">
					{ data.filters.length > 0 && (
						<>
							<h3>
								<Translation t="Pages.Collections.filter" />
							</h3>
							<div className="list">{
								data.filters.map( ( row , i ) => (
									<div key={i} className="filter">
										<select name={ row.name[ locale ].toLowerCase() } onChange={ (e)=>Filter.change(e) } placeholder={ row.name[ locale ] }>
											<option value="">{ row.name[ locale ] }</option>
											{
												data.filters[ i ].options.map( ( filter , j ) => (
													<option value={ filter } key={j}>
														{ filter }
													</option>
												))
											}
										</select>
									</div>
								))
							}</div>
						</>
					)}
					<CurrencySwitcher currency={ currency } callback={ ( currency ) => Filter.currency( currency ) }>
						<Button href="/account/wholesale" inverted size="narrow">
							<Translation t="Pages.Collections.wholesale" />
						</Button>
					</CurrencySwitcher>
				</div>
				<div data-view="mobile">
					{ data.filters.length > 0 && (
						<div>
							<a onClick={ () => Filter.mobile.open() } className="btn">
								Search Filters
							</a>
							<nav onClick={ (e) => Filter.mobile.closeModal(e) } data-filters-mobile>
								<div className="window">
									<header>
										<h3>
											<Translation t="Pages.Collections.filter" />
										</h3>
										<Button onClick={ ()=> Filter.mobile.close() }>{ t('Navigation.close') }</Button>
									</header>
									<div className="list">{
										data.filters.map( ( row , i ) => (
											<div key={i} className="filter">
												<h4>{ row.name[ locale ] }</h4>
												{
													data.filters[ i ].options.map( ( filter , j ) => (
														<div className="option" key={j}>
															<input type="radio" id={ `${ row.name[ locale ]  }-${  filter }` }  onChange={ (e)=>Filter.change(e) } checked={ filters[ row.name[ locale ].toLowerCase() ] == filter } value={ filter } name={ row.name[ locale ].toLowerCase() } />
															<label htmlFor={ `${ row.name[ locale ]  }-${  filter }`  }>{ filter }</label>
														</div>
													))
												}
											</div>
										))
									}</div>
									<footer>
										<Button onClick={ () => Filter.reset() } inverted>{ t('Pages.Collections.clear') }</Button>
										<Button onClick={ () => Filter.mobile.close() }>{ t('Pages.Collections.view') }</Button>
									</footer>
								</div>
							</nav>
						</div>
					)}
					<div>
						<CurrencySwitcher currency={ currency } callback={ ( currency ) => Filter.currency( currency ) } />
					</div>
				</div>
			</section>
			<div className="collection container">
				{ products.length == 0 && (
					<div className="none">
						<h2>{ t('Pages.Collections.none.title') }</h2>
						<p dangerouslySetInnerHTML={{ __html: t.raw('Pages.Collections.none.content') }} />
					</div>
				)}

				{ products.length > 0 && (
					<>
						{
							products.filter(
								product => ( product.variant.wholesale || product.productType != 'Essential Oil' )
							).map( ( product , i ) => {
								return <ProductCard key={ product.id } type={ currency } product={ product } />;
							})
						}
						{ ( () => {

							//
							const other = products.filter(
								product => ( !product.variant.wholesale && product.productType == 'Essential Oil' )
							);

							//If we have other
							if( other.length > 0 )
								return (
									<>
										<section className="divider">
											<h2>{ t('Pages.Collections.other.title') }</h2>
											<p>{ t.rich('Pages.Collections.other.description', Config.i18n.html) }</p>
										</section>
										{
											other.map( ( product , i ) => {
												return <ProductCard key={ product.id } type={ currency } product={ product } />;
											})
										}
									</>
								);

						})() }

					</>
				)}

				<Advertisement />

				<Preloader status={ loading } sticky />

			</div>
		</>
	);

}